import { useContext, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";

import CreateEditProjectHeader from "./CreateEditProjectHeader";
import CreateEditProjectForm from "./CreateEditProjectForm";
import BaseLoader from "../../../components/ui/BaseLoader";
import { LayoutContext } from "../../../context/LayoutContext";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createProject, getProjectById, updateProject } from "../../../services/api/project";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";

const validationSchema = Yup.object({
  title: Yup.string().required("Project Name is required"),
  managerName: Yup.string().required("Project Manager is required"),
  phoneNumber: Yup.string().required("Phone is required"),
  email: Yup.string()
    .required("Email is required")
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email format"),
  clientId: Yup.number().required("Client is required"),
  policies: Yup.string().required("Policies are required"),
  instructions: Yup.string().required("Instructions are required"),
});

const CreateEditProject = () => {
  const { isBannerVisible } = useContext(LayoutContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [isEditable, setIsEditable] = useState(false);

  const {
    isPending: isUpdateProjectApiLoading,
    data: updateProjectData,
    mutateAsync: updateProjectMutation,
  } = useMutation({
    mutationFn: updateProject,
    enabled: false,
  });

  const {
    isPending: isCreateProjectApiLoading,
    data: createProjectData,
    mutateAsync: createProjectMutation,
  } = useMutation({
    mutationFn: createProject,
    enabled: false,
  });

  const { isPending: isfetchProjectByIdApiLoading, data: projectData } = useQuery({
    queryKey: ["project", id],
    queryFn: () => getProjectById(id),
    enabled: !!id,
  });

  const selectedProject = projectData?.data;

  const formik = useFormik({
    initialValues: {
      title: selectedProject?.title || "",
      managerName: selectedProject?.managerName || "",
      phoneNumber: selectedProject?.phoneNumber || "",
      email: selectedProject?.email || "",
      policies: selectedProject?.policies || "",
      instructions: selectedProject?.instructions || "",
      projectContractFile: selectedProject?.projectContract || null,
      clientId: selectedProject?.clientId || null,
      isPoRequired: selectedProject?.isPoRequired || false,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleClientCreateOrUpdateButton(values);
    },
  });

  const handleClientCreateOrUpdateButton = async (values) => {
    try {
      const formData = new FormData();

      formData.append("title", values.title);
      formData.append("managerName", values.managerName);
      formData.append("email", values.email);
      formData.append("phoneNumber", values.phoneNumber);
      formData.append("policies", values.policies);
      formData.append("instructions", values.instructions);
      formData.append("clientId", values.clientId);
      formData.append("isPoRequired", values.isPoRequired);

      if (values.projectContractFile) {
        formData.append("projectContractFile", values.projectContractFile);
      }

      isEdit
        ? await updateProjectMutation({
            id,
            formData,
          })
        : await createProjectMutation(formData);
      setIsEditable(false);
      navigate("/workBoard/projects/");
    } catch (error) {
      dispatch(
        showToast({
          message: error.response.data.message,
          type: "error",
        }),
      );
    }
  };

  if (isEdit && isfetchProjectByIdApiLoading) {
    return <BaseLoader isLoading={isfetchProjectByIdApiLoading} />;
  }

  return (
    <div
      className={`bg-[#F6F6F6] h-full relative ${
        isBannerVisible ? "top-[107px]" : "sm:top-[56px]"
      }`}
    >
      <CreateEditProjectHeader
        onSave={formik.handleSubmit}
        isLoading={isUpdateProjectApiLoading || isCreateProjectApiLoading}
        isUpdated={formik.dirty}
        isEditable={isEditable}
        setIsEditable={setIsEditable}
        onCancel={() => {
          formik.resetForm();
          setIsEditable(false);
        }}
      />
      <CreateEditProjectForm formik={formik} isEditable={isEditable} isEditMode={isEdit} />
    </div>
  );
};

export default CreateEditProject;
